// KdsView — Phase H of the Client-Surfaces Programme.
// Kitchen Display System for the line: large cards, colour-coded by elapsed time,
// click to bump status. Designed for a wall-mounted tablet — no nav chrome.
//
// Reads /api/pos/dinein/orders, transitions via /api/pos/dinein/orders/{id}/status.
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Clock, ChefHat, ArrowRight, Loader2, RefreshCw, X, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 10000;

const FLOW = ['pending', 'in_progress', 'ready'];
const NEXT = { pending: 'in_progress', in_progress: 'ready' };

// Elapsed-minute thresholds (per service-floor good practice for QSR/fast-casual)
const toneFor = (mins, status) => {
  if (status === 'ready') return 'bg-ok/15 text-ok border-ok/40';
  if (mins < 8)  return 'bg-surface border-surface-border text-ink';
  if (mins < 15) return 'bg-warn/10 border-warn/40 text-warn';
  return 'bg-danger/10 border-danger/40 text-danger';
};

const since = (iso) => {
  if (!iso) return { mins: 0, label: '—' };
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return { mins, label: mins < 1 ? 'now' : `${mins}m` };
};

const KdsView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [audioOn, setAudioOn] = useState(() => localStorage.getItem('kdsAudioOn') !== '0');
  const [orgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Web Audio API beep — short, non-intrusive, no asset required.
  const audioCtxRef = useRef(null);
  const playBeep = useCallback(() => {
    if (!audioOn) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.value = 880;                  // A5
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
    } catch { /* audio not allowed yet — silent fail until user interacts */ }
  }, [audioOn]);

  const prevIdsRef = useRef(new Set());

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      FLOW.forEach(s => params.append('status', s));
      params.set('limit', '60');
      const res = await fetch(`${API_URL}/api/pos/dinein/orders?${params.toString()}`, { headers });
      const list = res.ok ? await res.json() : [];
      const arr = Array.isArray(list) ? list : (list.orders || []);
      const filtered = arr.filter(o => FLOW.includes(o.status));
      // Detect new pending orders since the last reload → audio bump.
      const ids = new Set(filtered.filter(o => o.status === 'pending').map(o => o.id || o._id || o.order_id));
      const prev = prevIdsRef.current;
      const isFirstLoad = prev.size === 0 && !loading;
      let newCount = 0;
      ids.forEach(id => { if (!prev.has(id)) newCount += 1; });
      prevIdsRef.current = ids;
      if (newCount > 0 && !isFirstLoad) playBeep();
      setOrders(filtered);
    } catch { /* stay with previous */ }
    finally { setLoading(false); }
  }, [headers, playBeep, loading]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-refresh every REFRESH_MS so the line stays live without manual reloads
  const tickRef = useRef(null);
  useEffect(() => {
    tickRef.current = setInterval(reload, REFRESH_MS);
    return () => clearInterval(tickRef.current);
  }, [reload]);

  const bump = async (order) => {
    const id = order.id || order._id || order.order_id;
    const next = NEXT[order.status];
    if (!next) return;
    setBusyId(id);
    setOrders(prev => prev.map(o => (o.id || o._id || o.order_id) === id ? { ...o, status: next } : o));
    try {
      await fetch(`${API_URL}/api/pos/dinein/orders/${id}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status: next })
      });
    } finally {
      setBusyId(null);
      reload();
    }
  };

  // Group by status
  const buckets = FLOW.reduce((acc, s) => { acc[s] = orders.filter(o => o.status === s); return acc; }, {});

  return (
    <div className="min-h-screen bg-ink text-surface" data-testid="kds-view-page">
      <header className="border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChefHat className="w-5 h-5 text-accent-500" />
          <div>
            <div className="text-xs uppercase tracking-overline opacity-70">Kitchen Display</div>
            <h1 className="font-display text-lg">Service line</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-data tabular-nums opacity-70" data-testid="kds-clock">
            {new Date().toLocaleTimeString()}
          </span>
          <button
            onClick={() => {
              const next = !audioOn;
              setAudioOn(next);
              localStorage.setItem('kdsAudioOn', next ? '1' : '0');
              if (next) playBeep(); // unlocks AudioContext + previews
            }}
            className={`px-3 py-1.5 text-xs rounded-md border ${audioOn ? 'border-accent-500/60 text-accent-500 bg-accent-500/10' : 'border-white/15 opacity-60 hover:bg-white/10'}`}
            title={audioOn ? 'Bump alerts on — click to mute' : 'Bump alerts muted — click to enable'}
            data-testid="kds-audio-toggle"
          >
            {audioOn ? '🔔 Sound on' : '🔕 Muted'}
          </button>
          <button onClick={reload} className="px-3 py-1.5 text-xs rounded-md border border-white/15 hover:bg-white/10" data-testid="kds-refresh">
            <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {FLOW.map(status => (
          <section key={status} data-testid={`kds-column-${status}`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-base uppercase tracking-overline opacity-80">
                {status.replace('_', ' ')}
              </h2>
              <span className="text-xs font-data tabular-nums opacity-60">{buckets[status].length}</span>
            </div>
            <div className="space-y-3">
              {buckets[status].length === 0 ? (
                <p className="text-xs opacity-40 italic">Empty</p>
              ) : buckets[status].map(o => {
                const id = o.id || o._id || o.order_id;
                const { mins, label } = since(o.created_at);
                const tone = toneFor(mins, o.status);
                return (
                  <article
                    key={id}
                    onClick={() => bump(o)}
                    className={`rounded-md border px-3 py-3 cursor-pointer motion-organic ${tone} ${busyId === id ? 'opacity-60' : 'hover:scale-[1.01]'}`}
                    data-testid={`kds-card-${id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base">
                          {o.table_label || o.table_number || `Order ${String(id).slice(0, 6)}`}
                        </div>
                        <div className="text-xs opacity-70">{o.customer_name || ''}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-overline opacity-70">elapsed</div>
                        <div className="font-data tabular-nums text-base" data-testid={`kds-card-${id}-elapsed`}>
                          <Clock className="w-3 h-3 inline mr-0.5" />{label}
                        </div>
                      </div>
                    </div>
                    {Array.isArray(o.items) && o.items.length ? (
                      <ul className="mt-2 text-sm space-y-0.5">
                        {o.items.slice(0, 6).map((it, i) => (
                          <li key={i} className="flex items-baseline gap-2">
                            <span className="font-data tabular-nums w-5">{it.quantity || 1}×</span>
                            <span className="flex-1 truncate">{it.name || it.menu_item_id}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {NEXT[o.status] ? (
                      <div className="mt-3 text-xs opacity-80 flex items-center gap-1">
                        Tap to mark <strong>{NEXT[o.status].replace('_', ' ')}</strong>
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    ) : status === 'ready' ? (
                      <div className="mt-3 text-xs opacity-80 flex items-center gap-1 text-ok">
                        <CheckCircle2 className="w-3 h-3" /> Ready to pick up
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default KdsView;
